import React from 'react';
import { useStyletron } from 'baseui';
import { BooleanColumn, NumericalColumn, CustomColumn, CategoricalColumn } from 'baseui/data-table';
import { isNil } from 'lodash';
import { GAME_NAME } from 'commons/superApp/models/games';
import { EHappyHourTypes, THappyHourTemplateWithSort, ScreenName } from '../../interfaces';

export const COL_NAMES: Record<string, keyof THappyHourTemplateWithSort> = {
	bgColor: 'bgColor',
	ctaBgColor: 'ctaBgColor',
	ctaDisplayText: 'ctaDisplayText',
	ctaTextColor: 'ctaTextColor',
	ctaType: 'ctaType',
	ctaValue: 'ctaValue',
	dividerColor: 'dividerColor',
	endHour: 'endHour',
	endTime: 'endTime',
	fgUrl: 'fgUrl',
	gameName: 'gameName',
	hhType: 'hhType',
	imageUrl: 'imageUrl',
	isDisabled: 'isDisabled',
	marqueeAnimUrl: 'marqueeAnimUrl',
	marqueeBgColor: 'marqueeBgColor',
	marqueeText: 'marqueeText',
	marqueeTextColor: 'marqueeTextColor',
	marqueeWiggleColor: 'marqueeWiggleColor',
	priority: 'priority',
	screenName: 'screenName',
	startHour: 'startHour',
	startTime: 'startTime',
	stripeColor: 'stripeColor',
	stripeUrl: 'stripeUrl',
	subtitle: 'subtitle',
	text1: 'text1',
	text1Color: 'text1Color',
	text2: 'text2',
	text2Color: 'text2Color',
	timeText: 'timeText',
	timeTextColor: 'timeTextColor',
	titleUrl: 'titleUrl',
	sortOrder: 'sortOrder'
};

const ctaConfigs = [
	CustomColumn({
		title: 'CTA BG Color',
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.ctaBgColor],
		maxWidth: 200,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	}),
	CategoricalColumn({
		title: 'CTA DisplayText',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.ctaDisplayText] as string
	}),
	CustomColumn({
		title: 'CTA Text Color',
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.ctaTextColor],
		maxWidth: 200,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	}),
	CategoricalColumn({
		title: 'CTA Type',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.ctaType] as string
	}),
	CategoricalColumn({
		title: 'CTA Value',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.ctaValue] as string
	})
];

const primaryTextConfigs = [
	CategoricalColumn({
		title: 'Primary Text',
		maxWidth: 300,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.text1] as string
	}),
	CustomColumn({
		title: 'Primary Text Color',
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.text1Color],
		maxWidth: 100,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	})
];

const secondaryTextConfig = [
	CategoricalColumn({
		title: 'Secondary Text',
		maxWidth: 300,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.text2] as string
	}),
	CustomColumn({
		title: 'Secondary Text Color',
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.text2Color],
		maxWidth: 100,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	})
];

const timeConfig = [
	CategoricalColumn({
		title: 'Start Time',
		maxWidth: 220,
		mapDataToValue: (data: THappyHourTemplateWithSort) => {
			if (!isNil(data[COL_NAMES.startTime])) {
				return new Date(data[COL_NAMES.startTime] as number).toString();
			}
			return 'No Start time';
		}
	}),
	CategoricalColumn({
		title: 'End Time',
		maxWidth: 220,
		mapDataToValue: (data: THappyHourTemplateWithSort) => {
			if (!isNil(data[COL_NAMES.endTime])) {
				return new Date(data[COL_NAMES.endTime] as number).toString();
			}
			return 'Infinite';
		}
	}),
	CategoricalColumn({
		title: 'Start Hour',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.startHour] as string
	}),
	CategoricalColumn({
		title: 'End Hour',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.endHour] as string
	})
];

const generalConfig = [
	NumericalColumn({
		title: 'Priority',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.priority] as number
	}),

	CategoricalColumn({
		title: 'Screen Name',
		maxWidth: 300,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.screenName] as ScreenName
	}),
	CategoricalColumn({
		title: 'Game Name',
		maxWidth: 300,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.gameName] as GAME_NAME
	}),
	CategoricalColumn({
		title: 'Happy Hour Type',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.hhType] as EHappyHourTypes
	}),
	NumericalColumn({
		title: 'Sort Order',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => (data[COL_NAMES.sortOrder] || 0) as number
	}),
	CategoricalColumn({
		title: 'Subtitle',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.subtitle] as string
	}),
	BooleanColumn({
		title: 'Is Active',
		maxWidth: 200,
		mapDataToValue: (data: THappyHourTemplateWithSort) => !data[COL_NAMES.isDisabled]
	})
];

export const columnConfigs = [
	CustomColumn({
		title: 'BG Color',
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.bgColor],
		maxWidth: 200,
		renderCell: (params: any) => {
			const { value } = params;
			const [css] = useStyletron();
			return (
				<div
					className={css({
						backgroundColor: value,
						height: '30px',
						width: '100%'
					})}
				/>
			);
		}
	}),
	...ctaConfigs,
	...timeConfig,

	CustomColumn({
		title: 'Bonus Shine',
		minWidth: 300,
		maxWidth: 300,
		mapDataToValue: (data: THappyHourTemplateWithSort) => data[COL_NAMES.imageUrl],
		renderCell: ({ value }: { value: any }) => {
			const [css] = useStyletron();
			return (
				<a href={value} target='_blank' rel='noreferrer'>
					<img src={value} alt='' className={css({ height: '90px' })} />
				</a>
			);
		}
	}),
	...primaryTextConfigs,
	...secondaryTextConfig,
	...generalConfig
];

export const activeFilter = [
	{
		columnKey: 'Is Active',
		filterLabel: 'Active Happy Hours',
		filterValue: true
	}
];
